<template>
  <div class="root-novel-list">
    <div class="novel-list-header">
      <h2>{{ $t("homePage.novelList.title") }}</h2>
      <div class="pc flex flex-center">
        <div class="flex novel-import" @click="onClickImportFile()">
          <IconUpload class="flex flex-center mr-5 icon-import" />
          <span>{{ $t("homePage.novelList.importWork") }}</span>
        </div>
        <memo-button />
      </div>
    </div>

    <novel-list-toolbar
      class="toolbar"
      :search-query="searchQuery"
      :sort-key="sortKey"
      :sort-order="sortOrder"
      :layout="layout"
      :has-active-filter="hasActiveFilter"
      @update:searchQuery="onUpdateSearchQuery"
      @update:sortKey="onUpdateSortKey"
      @update:sortOrder="onUpdateSortOrder"
      @update:layout="onUpdateLayout"
      @open-filter="onOpenFilter"
    />
    <div class="list-container">
      <div v-if="novels" class="list" :class="{ 'list-view': layout === 'list' }">
        <template v-for="novel in novels">
          <novel-card
            v-if="layout === 'grid'"
            class="novel-item"
            :novel="novel"
            :key="novel.novelId"
            :is-menu-open="openMenuId === novel.novelId"
            @toggle-menu-click="handleToggleMenu"
            @changeLoading="changeLoading"
          />
          <novel-list-item
            v-else
            :novel="novel"
            :key="'list-' + novel.novelId"
            :is-menu-open="openMenuId === novel.novelId"
            @toggle-menu-click="handleToggleMenu"
            @changeLoading="changeLoading"
          />
        </template>
      </div>
      <button-round
        :to="{ name: 'novelCreate' }"
        class="novel-create tutorial-novel-create"
        :icon="require('@/assets/img/icon/add_book.svg')"
        :size="40"
      >
        <div class="pc remove-margin" />
        <span class="sp">{{ $t("homePage.novelList.newWork") }}</span>
      </button-round>
    </div>

    <masked-loading v-if="isLoading" class="loading" />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import MemoButton from "@/components/atoms/MemoButton.vue";
import NovelCard from "@/components/atoms/NovelCover.vue";
import NovelListItem from "@/components/atoms/NovelListItem.vue";
import ButtonRound from "@/components/atoms/ButtonRound.vue";
import NovelListToolbar from "@/components/molecules/NovelListToolbar.vue";
import { workStatus as workStatusList } from "@/lib/novelSettings";
import { Novel, NovelSetting } from "@/lib/models";
import { NovelGroupSummary } from "@/lib/models/novelGroup";
import clone from "lodash/cloneDeep";
import { sortAndPinNovels } from "@/lib/utils/novelListSort";
import { readManuscriptFiles, ManuscriptImportPayload } from "@/lib/utils/manuscriptFileImport";
import { Dialog } from "@/lib/utils";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import NovelFilterDialog, { NovelFilterDialogResult } from "@/components/ui/dialogs/NovelFilterDialog.vue";
import ImportDialog, { ImportDialogResult } from "@/components/ui/dialogs/ImportDialog.vue";
import MaskedLoading from "@/components/atoms/MaskedLoading.vue";
import IconUpload from "~icons/figma/Upload";

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { MemoButton, NovelCard, NovelListItem, ButtonRound, NovelListToolbar, IconUpload, MaskedLoading },
  data() {
    return {
      workStatusList,
      searchQuery: "",
      filterSearchQuery: "",
      searchDebounceTimer: null as number | null,
      openMenuId: null,
      isLoading: false,
    };
  },
  computed: {
    novels() {
      let novels = clone(this.$store.getters["novelModule/novels"] as Novel[]);

      const { genres, categories, workStatuses, filterGroups, filterSearchQuery, sortKey, sortOrder, workStatusList } = this;

      /** タイトル検索 */
      if (filterSearchQuery) {
        // macOS 由来の NFD ファイル名から取り込んだタイトルは NFC 化済みだが、
        // 既存データ（NFD のまま保存済み）でも検索がヒットするよう両辺を NFC 正規化して比較する。
        const normalizedQuery = filterSearchQuery.normalize("NFC");
        novels = novels.filter((novel) => novel.title.normalize("NFC").includes(normalizedQuery));
      }

      /** フィルタリング */
      if (genres.length > 0) {
        novels = novels.filter((novel) => genres.some((g) => novel.genre === g.id));
      }
      if (categories.length > 0) {
        novels = novels.filter((novel) => categories.some((c) => novel.category && novel.category.includes(c.id)));
      }
      if (workStatuses.length > 0) {
        novels = novels.filter((novel) =>
          workStatuses.some((ws) => {
            if (ws.id === workStatusList[0].id) {
              return novel.workStatus === null || novel.workStatus === ws.id;
            }
            return novel.workStatus === ws.id;
          })
        );
      }
      if (filterGroups.length > 0) {
        novels = novels.filter((novel) =>
          filterGroups.some((fg) => novel.groups?.some((g) => g.groupId === fg.groupId))
        );
      }

      return sortAndPinNovels(novels, sortKey, sortOrder);
    },
    genres() {
      return this.$store.getters["novelModule/genres"];
    },
    categories() {
      return this.$store.getters["novelModule/categories"];
    },
    workStatuses() {
      return this.$store.getters["novelModule/workStatuses"];
    },
    filterGroups() {
      return this.$store.getters["novelModule/filterGroups"];
    },
    sortKey() {
      return this.$store.getters["novelModule/novelListSortKey"];
    },
    sortOrder() {
      return this.$store.getters["novelModule/novelListSortOrder"];
    },
    layout() {
      return this.$store.getters["novelModule/novelListLayout"];
    },
    hasActiveFilter() {
      return this.genres.length > 0 || this.categories.length > 0 || this.workStatuses.length > 0 || this.filterGroups.length > 0;
    },
  },
  beforeDestroy() {
    if (this.searchDebounceTimer !== null) {
      window.clearTimeout(this.searchDebounceTimer);
    }
  },
  methods: {
    onUpdateSearchQuery(value: string) {
      this.searchQuery = value;
      if (this.searchDebounceTimer !== null) {
        window.clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = window.setTimeout(() => {
        this.filterSearchQuery = value;
      }, 300);
    },
    onUpdateSortKey(value: string) {
      this.$store.dispatch("novelModule/updateNovelListSortKey", value);
    },
    onUpdateSortOrder(value: "asc" | "desc") {
      this.$store.dispatch("novelModule/updateNovelListSortOrder", value);
    },
    onUpdateLayout(value: "grid" | "list") {
      this.$store.dispatch("novelModule/updateNovelListLayout", value);
    },
    async onOpenFilter() {
      const filterDialog = new Dialog(NovelFilterDialog);
      const result: NovelFilterDialogResult | null = await filterDialog.show({
        workStatuses: this.workStatuses,
        genres: this.genres,
        categories: this.categories,
        groups: this.filterGroups,
      });
      if (result) {
        this.$store.dispatch("novelModule/updateNovelListFilters", result);
      }
    },
    async onClickImportFile() {
      const importDialog = new Dialog(ImportDialog);
      const result: ImportDialogResult | null = await importDialog.show();
      if (!result) return;

      await this.runImport(result.files, result.title, result.isScriptMode);
    },
    async runImport(files: File[], title: string, isScriptMode: boolean) {
      // インポートフラグを立てる
      this.$emit("import-start");

      try {
        this.$emit("changeLoading", true);

        // 先に全ファイルのパースを完了させ、成功した場合のみ作品を作成する。
        // （createNovel を先に走らせると、docx のパース失敗時に空の作品だけが残るため）
        const payloads = await readManuscriptFiles(files, isScriptMode);

        // 作品の新規作成（作品名はダイアログで決定済み。フォルダインポートはフォルダ名、
        // ファイルインポートは先頭ファイル名。脚本モードはトグルに従う）
        const novelId = await this.createNovel(title, isScriptMode ? "script" : "default");

        await this.createMultipleManuscript(novelId, payloads);

        await this.$store.dispatch("novelModule/initialize");

        const completeDialog = new Dialog(SimpleDialog);
        const completeData: SimpleDialogProps = {
          title: this.$t("homePage.novelList.import.successTitle"),
          content: this.$t("homePage.novelList.import.successMessage"),
          close: this.$t("homePage.novelList.import.successButton"),
          actionHandler: () => {
            this.$emit("import-end");
            this.$router.push({ name: "editor", params: { novelId } });
          },
          width: 350,
        };
        completeDialog.show(completeData);
      } catch (error) {
        console.error(error);

        const errorDialog = new Dialog(SimpleDialog);
        const errorData: SimpleDialogProps = {
          title: this.$t("homePage.novelList.import.errorTitle"),
          content: this.$t("homePage.novelList.import.errorMessage"),
          isError: true,
          width: 350,
        };
        errorDialog.show(errorData);
      } finally {
        this.$emit("changeLoading", false);
      }
    },
    async createNovel(title, manuscriptMode = "default") {
      const { novelId } = (await this.$store.dispatch("novelModule/createNovel", {
        title,
        manuscriptMode,
      })) as Novel;

      return novelId;
    },
    async createMultipleManuscript(novelId, payload) {
      const params = payload.map((item) => ({
        novelId,
        title: item.title || "",
        content: item.content || "",
      }));

      await this.$store.dispatch("manuscriptModule/createMultipleManuscript", {
        params,
      });
    },
    handleToggleMenu(novelId) {
      this.openMenuId = this.openMenuId === novelId ? null : novelId;
    },
    changeLoading(value) {
      this.isLoading = value;
    },
  },
});

interface Data {
  workStatusList: NovelSetting[];
  searchQuery: string;
  filterSearchQuery: string;
  searchDebounceTimer: number | null;
  openMenuId: null | string;
  isLoading: boolean;
}

interface Computed {
  novels: Novel[];
  genres: NovelSetting[];
  categories: NovelSetting[];
  workStatuses: NovelSetting[];
  filterGroups: NovelGroupSummary[];
  sortKey: string;
  sortOrder: "asc" | "desc";
  layout: "grid" | "list";
  hasActiveFilter: boolean;
}

interface Methods {
  onUpdateSearchQuery(value: string): void;
  onUpdateSortKey(value: string): void;
  onUpdateSortOrder(value: "asc" | "desc"): void;
  onUpdateLayout(value: "grid" | "list"): void;
  onOpenFilter(): Promise<void>;
  onClickImportFile(): Promise<void>;
  runImport(files: File[], title: string, isScriptMode: boolean): Promise<void>;
  createNovel(title: string, manuscriptMode?: "default" | "script"): Promise<string>;
  createMultipleManuscript: (novelId: string, payload: ManuscriptImportPayload[]) => Promise<void>;
  handleToggleMenu(novelId: string): void;
  changeLoading(value: boolean): void;
}
</script>

<style lang="scss" scoped>
.sp {
  display: none !important;
}
@media only screen and (max-width: 750px) {
  .pc {
    display: none !important;
  }
  .sp {
    display: block !important;
  }
}

@mixin novelList($width, $padding, $grid-gap) {
  h2 {
    margin-left: $padding;
    font-size: var(--font-size-xl);
    @include mq(sp) {
      font-size: var(--font-size-md);
    }
  }

  .novel-import {
    box-sizing: border-box;
    border: 2px solid;
    border-radius: var(--radius-rounded);
    padding: 8px 24px;
    margin-right: 10px;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    cursor: pointer;
    color: var(--color-text-normal);
  }

  .toolbar {
    margin: 0 $padding;
  }

  .list {
    --novel-list-padding-x: #{$padding};
    position: relative;
    display: grid;
    grid-gap: $grid-gap;
    padding: 0 var(--novel-list-padding-x) $grid-gap;
    grid-template-columns: repeat(auto-fill, minmax($width, 1fr));

    &.list-view {
      grid-template-columns: 1fr;
      grid-gap: 0;
    }
  }

  .novel-create {
    @include mq(pc) {
      right: 50px;
      bottom: 50px;
    }

    @include mq(sp) {
      bottom: 0;
    }
  }
}

@include mq(sp) {
  @include novelList($width: 150px, $padding: 15px, $grid-gap: 10px);
}

@include mq(pc) {
  @include novelList($width: 150px, $padding: 50px, $grid-gap: 20px);
}

@include mq(sm) {
  // tablet
  @include novelList($width: 180px, $padding: 50px, $grid-gap: 40px);
}

@include mq(md) {
  // tablet
  @include novelList($width: 200px, $padding: 50px, $grid-gap: 40px);
}

@include mq(pc) {
  .root-novel-list {
    height: calc(100vh - #{$header-height});
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    position: relative;

    .list-container {
      flex-grow: 1;
      overflow: auto;

      .list {
        margin-bottom: 150px;
      }
    }
  }
}

@include mq(sp) {
  .list {
    padding-bottom: 50px;
  }
}

.novel-list-header {
  @include mq(pc) {
    padding-right: 2rem;
  }
  @include mq(sp) {
    padding-right: 1rem;
  }

  display: flex;
  justify-content: space-between;
}

.novel-card {
  margin: 0;
  width: 100%;
  height: 100%;

  // @include mq(sp) {
  //   width: $width * $sp-scale;
  //   height: $height * $sp-scale;
  //   margin: $margin-s;
  // }
}
button {
  background-color: none;
  border: none;
  display: inline;
}
.help {
  border: bold 0.5px var(--color-border);
}

/*
.create {
  background-color: #dddddd;
  border-radius: var(--radius-xs);
  text-align: center;
  position: relative;
  text-decoration: none;

  &:hover {
    background-color: #aaaaaa;
  }

  .new-novel {
    display: flex;
    justify-content: center;
    align-items: stretch;
    font-size: larger;
    color: #333;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .text {
    vertical-align: middle;
    line-height: $height;
    height: $height;
    text-decoration: none;
    /* @include mq(sp) {
      line-height: $height * $sp-scale;
      height: $height * $sp-height-scale;
      width: 150px;
      font-size: var(--font-size-xs);
    }
  }
}
*/
/*ボックス全体*/
.accbox {
  margin: 0 100px;
  margin-bottom: 50px;
  padding: 0;
  width: calc(100% - 200px);
  @include mq(sp) {
    margin: 0 10px;
    width: calc(100% - 20px);
  }
  @include mq(sm) {
    margin: 0 50px;
    width: calc(100% - 100px);
  }
  @include mq(md) {
    margin: 0 50px;
    width: calc(100% - 100px);
  }
}

/*ラベル*/
.accbox label {
  display: block;
  margin: 1px 0;
  padding: 11px 12px;
  color: var(--color-text-normal);
  font-weight: var(--font-weight-bold);
  border: var(--color-border) solid 0.5px;
  border-radius: var(--radius-xs);
  background: var(--color-surface-base);
  cursor: pointer;
  transition: all 0.5s;
}

/*ラベルホバー時*/
.accbox label:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-normal);
  border-radius: var(--radius-xs);
}

/*チェックは隠す*/
.accbox input {
  display: none;
}

/*中身を非表示にしておく*/
.accbox .accshow {
  height: 0;
  padding: 0px 15px;
  overflow: hidden;
  opacity: 0;
  transition: 0.5s;
}

/*クリックで中身表示*/
.cssacc:checked + .accshow {
  height: auto;
  padding: 5px 15px;
  //background: var(--color-surface-base);
  color: var(--color-text-normal);
  border-top: var(--color-border);
  opacity: 1;
}

.flex {
  display: flex;
}

.flex-center {
  align-items: center;
  justify-content: center;
}

.mr-5 {
  margin-right: 5px;
}

.icon-import {
  font-size: 20px;
}

.loading {
  z-index: 100;
}
</style>
